<template>
    <div
        ref="groupRef"
        class="el-radio-group"
        :class="{ 'is-invalid': isInvalid }"
        role="radiogroup"
        :aria-invalid="isInvalid || null"
        @keydown="handleKeydown"
    >
        <div class="el-radio-group__content">
            <slot />
        </div>
    </div>
</template>

<script setup>
import { computed, ref, toRef, provide, inject, watch, onMounted, nextTick } from 'vue';

const emit = defineEmits(['update:modelValue', 'change']);
const props = defineProps({
    modelValue: {type: [String, Number, Boolean], default: false },
    disabled: Boolean,
});

const elFormItem = inject('elFormItem', null);
const groupRef = ref(null);
const isInvalid = computed(() => elFormItem?.validateState?.value === 'error');

watch(() => props.modelValue, () => {
    elFormItem?.markAsTouched?.();
    elFormItem?.validate?.('change');
    elFormItem?.validate?.('blur');
    emit('change', props.modelValue);
});

provide('radioGroup', {
    modelValue: toRef(props, 'modelValue'),
    disabled: props.disabled,
    updateModelValue: (val) => emit('update:modelValue', val),
    emitChange: (val) => emit('change', val),
});

onMounted(() => {
    nextTick(() => {
        const group = groupRef.value;
        if (!group) return;

        const labels = [...group.querySelectorAll('[role=radio]')];
        const anyChecked = group.querySelector('[type=radio]:checked');

        if (!anyChecked && labels.length) {
            labels[0].setAttribute('tabindex', '0');
        }
    });
});

function handleKeydown(e) {
    const radios = Array.from(groupRef.value?.querySelectorAll('[role=radio]') || []);
    const index = radios.indexOf(e.target);
    const total = radios.length;
    if (index === -1) return;

    const next = (i) => {
        const el = radios[i];
        el?.querySelector('input')?.focus();
        el?.focus();
    };

    switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
            e.preventDefault();
            next((index - 1 + total) % total);
            break;
        case 'ArrowRight':
        case 'ArrowDown':
            e.preventDefault();
            next((index + 1) % total);
            break;
    }
}
</script>

<style lang="postcss" scoped>
.el-radio-group {
    display: flex;
    width: 100%;
    &__content {
        display: flex;
        gap: 16px;
    }
    &.is-invalid {
        position: relative;
        display: flex;
        padding: 0 8px;
        &:after {
            content: "";
            display: block;
            border: 1px solid var(--av-fixed-danger);
            border-radius: 4px;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            pointer-events: none;
        }
    }
}
</style>
